import React, { useState, useEffect, useRef } from 'react';
import { Loader2, CheckCircle, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import { useEmailStatus } from '../hooks/useFeedbackInstanceApi';
import EmailErrorDetails from './EmailErrorDetails';
import EmailRecipientsDetails from './EmailRecipientsDetails';

interface EmailStatusBadgeProps {
  instanceId: string;
  isPolling: boolean;
  onPollComplete?: () => void;
}

const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const EmailStatusBadge: React.FC<EmailStatusBadgeProps> = ({
  instanceId,
  isPolling,
  onPollComplete,
}) => {
  const [timedOut, setTimedOut] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const pollStartRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: status } = useEmailStatus(instanceId, isPolling && !timedOut);

  useEffect(() => {
    if (isPolling && !pollStartRef.current) {
      pollStartRef.current = Date.now();
      timeoutRef.current = setTimeout(() => {
        setTimedOut(true);
        onPollComplete?.();
      }, POLL_TIMEOUT_MS);
    }

    if (!isPolling) {
      pollStartRef.current = null;
      setTimedOut(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isPolling, onPollComplete]);

  useEffect(() => {
    if (status && (status.status === 'completed' || status.status === 'failed')) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      onPollComplete?.();
    }
  }, [status, onPollComplete]);

  if (!status && !isPolling) return null;

  if (timedOut) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-2">
        <HelpCircle className="h-3.5 w-3.5" />
        <span>Status unknown — refresh to check</span>
      </div>
    );
  }

  if (!status || status.status === 'pending' || status.status === 'processing') {
    if (!isPolling) return null;
    return (
      <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 mt-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Sending{status ? ` (${status.sent}/${status.totalRecipients})` : ''}...</span>
      </div>
    );
  }

  if (status.status === 'failed' && status.sent === 0) {
    return (
      <>
        <button
          onClick={() => status.errors?.length && setShowErrors(true)}
          className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 mt-2 hover:underline"
        >
          <XCircle className="h-3.5 w-3.5" />
          <span>Failed</span>
        </button>
        {showErrors && status.errors && (
          <EmailErrorDetails errors={status.errors} onClose={() => setShowErrors(false)} />
        )}
      </>
    );
  }

  if (status.failed > 0) {
    return (
      <>
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => setShowRecipients(true)}
            className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:underline"
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            <span>{status.sent} sent, {status.failed} failed</span>
          </button>
        </div>
        {showRecipients && status.recipients && (
          <EmailRecipientsDetails recipients={status.recipients} onClose={() => setShowRecipients(false)} />
        )}
        {showErrors && status.errors && (
          <EmailErrorDetails errors={status.errors} onClose={() => setShowErrors(false)} />
        )}
      </>
    );
  }

  return (
    <>
      <button
        onClick={() => setShowRecipients(true)}
        className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 mt-2 hover:underline"
      >
        <CheckCircle className="h-3.5 w-3.5" />
        <span>{status.sent} sent</span>
      </button>
      {showRecipients && status.recipients && (
        <EmailRecipientsDetails recipients={status.recipients} onClose={() => setShowRecipients(false)} />
      )}
    </>
  );
};

export default EmailStatusBadge;
